import { InvitationsApiService } from './../../../../server-api/invitations-api.service';
import { Component, OnInit } from '@angular/core';
import { RegisterUserInput } from '../../../../models/User/register-user-input';
import { OperationError } from '../../../../models/server-result/operation-error';
import { PublicCompany } from '../../../../models/companies/public-company';
import { Company } from '../../../../models/companies/company';
import { User } from '../../../../models/User/user';
import { CodeDecodeValue } from '../../../../models/common/code-decode-value';
import { ServerApiService } from '../../../../server-api/server-api.service';
import { LogService } from '../../../../services/log/log.service';
import { ToasterService } from '../../../../shared-components/custom-toaster/toaster.service';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '../../../../services/translate/translate.service';
import { UtilitiesService } from '../../../../services/utils/utilities.service';
import { AppContextService } from '../../../../services/app-context.service';
import { OperationResult } from '../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { TimeZoneValue } from '../../../../models/common/time-zone-value';
import { AcceptNewCompanyInviteForConfirmingNetwork } from '../../../../models/invitations/confirming/accept-new-company-invite-confirming-network';
import { RefuseNewCompanyInviteForConfirmingNetwork } from '../../../../models/invitations/confirming/refuse-new-company-invite-confirming-network';
import { PublicInviteCompanyForNetworkTicket } from '../../../../models/invitations/public-invite-company-network-ticket';
import { InvitationStatus } from '../../../../models/enums/Invitations/invitation-status';
import { CompanyLegalAcceptance } from '../../../../models/funding-request/company-legal-acceptance';
import { InvitationToken } from '../../../../models/invitations/token';
import { PersonalDataConsentLinkAcceptance } from '../../../../server-api/user-api/user-api.models';

@Component({
	selector: 'app-confirming-buyer-invitation-response',
	templateUrl: './buyer-invitation-response.component.html',
	styleUrls: ['./buyer-invitation-response.component.scss'],
	standalone: false,
})
export class ConfirmingBuyerInvitationResponseComponent implements OnInit {
	private invitationId: string;
	public invitedUserEmail: string;
	private publicInviteCompanyForNetworkTicket: PublicInviteCompanyForNetworkTicket = new PublicInviteCompanyForNetworkTicket();

	public newUser: boolean;
	public registerUserInput: RegisterUserInput = new RegisterUserInput();
	public isPerformingRequest: boolean = false;
	public isSubmittingAccept: boolean = false;
	public isSubmittingRefuse: boolean = false;
	public requestErrors: OperationError[] = [];
	public invitingCompany: PublicCompany = new PublicCompany();
	public invitedCompany: Company = new Company();
	public invitedUser: User = new User();
	public languageCodes: CodeDecodeValue[];
	public timeZones: TimeZoneValue[];

	public showSucessSubmission: boolean = false;
	public sucessSubmissionContent: string | null = null;
	public sucessSubmissionButtonText: string | null = null;

	public token = new InvitationToken();

	private personalDataConsentDocumentAcceptance: PersonalDataConsentLinkAcceptance | null = null;
	private legalUserAcceptance: CompanyLegalAcceptance | null = null;
	public pageDisplayLanguageCode: string | null = null;

	constructor(
		private serverApiService: ServerApiService,
		private invitationsApiService: InvitationsApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private route: ActivatedRoute,
		private translateService: TranslateService,
		private utilitiesService: UtilitiesService,
		private appContext: AppContextService
	) {}

	ngOnInit() {
		this.pageDisplayLanguageCode = this.appContext.getApplicationLanguageCode();

		this.route.params.subscribe((params) => {
			let base64InvitationContext = params['invitationContext'];

			if (!base64InvitationContext) {
				this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_INVITATION_CONTEXT_FROM_ROUTE', 'list-errorCodes'));
			}

			let invitationContext = atob(base64InvitationContext);

			if (!!invitationContext) {
				let parameters: string[] = invitationContext.split('&');

				if (parameters.length < 3)
					this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_INVITATION_CONTEXT_CONTENT', 'list-errorCodes'));
				else {
					this.token.Id = parameters[0].split(':')[1];
					this.invitationId = parameters[1].split(':')[1];
					this.invitedUserEmail = parameters[2].split(':')[1];

					if (!!this.token.Id && !!this.invitationId && !!this.invitedUserEmail) {
						this.isPerformingRequest = true;

						const getCompanyInvitationTicketServiceCall = this.invitationsApiService.getCompanyInvitationTicket(
							this.invitationId,
							this.token.Id
						);
						const getTimeZonesServiceCall = this.serverApiService.getTimeZones();
						const languageCodesServiceCall = this.serverApiService.getLanguageCodes();

						let servicesCallsToMake: Array<
							| Observable<OperationResult<PublicInviteCompanyForNetworkTicket>>
							| Observable<OperationResult<TimeZoneValue[]>>
							| Observable<OperationResult<CodeDecodeValue[]>>
						> = [];

						servicesCallsToMake.push(getCompanyInvitationTicketServiceCall);
						servicesCallsToMake.push(getTimeZonesServiceCall);
						servicesCallsToMake.push(languageCodesServiceCall);

						const getAllRequests = forkJoin(servicesCallsToMake);

						getAllRequests.subscribe((opResults) => {
							const getCompanyInvitationTicketResult = opResults[0] as OperationResult<PublicInviteCompanyForNetworkTicket>;
							const getTimeZonesResult = opResults[1] as OperationResult<TimeZoneValue[]>;
							const languageCodesResult = opResults[2] as OperationResult<CodeDecodeValue[]>;

							if (!getCompanyInvitationTicketResult.IsValid) {
								this.processInvalidResult(getCompanyInvitationTicketResult);
								this.isPerformingRequest = false;
								return;
							}

							if (!getTimeZonesResult.IsValid) {
								this.processInvalidResult(getTimeZonesResult);
								this.isPerformingRequest = false;
								return;
							}

							if (!languageCodesResult.IsValid) {
								this.processInvalidResult(languageCodesResult);
								this.isPerformingRequest = false;
								return;
							}

							if (getCompanyInvitationTicketResult.IsValid && getTimeZonesResult.IsValid && languageCodesResult.IsValid) {
								this.publicInviteCompanyForNetworkTicket = getCompanyInvitationTicketResult.Data;

								this.token.expired = this.publicInviteCompanyForNetworkTicket.IsExpired;

								if (this.token.expired) {
									this.showInvitationContextInformationWhenTokenUsedOrExpired(getCompanyInvitationTicketResult);
									this.retrivedTokenAsExpiredMessages();
									this.isPerformingRequest = false;
									return;
								}

								this.token.used = this.publicInviteCompanyForNetworkTicket.InvitationStatus !== InvitationStatus.Processed;

								if (this.token.used) {
									this.showInvitationContextInformationWhenTokenUsedOrExpired(getCompanyInvitationTicketResult);
									this.retrivedTokenUsedMessages();
									this.isPerformingRequest = false;
									return;
								}

								this.newUser = !getCompanyInvitationTicketResult.Data.InvitedExistingUser;
								this.invitingCompany = this.publicInviteCompanyForNetworkTicket.InvitingCompany;
								this.languageCodes = languageCodesResult.Data;
								this.timeZones = this.utilitiesService.GetOrderedTimeZoneValues(getTimeZonesResult.Data, false);

								if (!!this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany) {
									this.invitedCompany = <Company>{
										Id: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.Id,
										Name: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.Name,
										VatNumber: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.VatNumber,
										CountryCode: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.CountryCode,
										LanguageCode: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.LanguageCode,
										TimeZone: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.TimeZone,
										AddressLine1: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.AddressLine1,
										AddressLine2: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.AddressLine2,
										AddressLine3: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.AddressLine3,
										ZipCode: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.ZipCode,
										City: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.City,
										ActivityCodeList: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.ActivityCodeList,
										ActivityCode: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.ActivityCode,
										ShareCapital: this.publicInviteCompanyForNetworkTicket.InvitedExistingCompany.ShareCapital,
									};
								} else {
									this.invitedCompany = <Company>{
										CountryCode: this.publicInviteCompanyForNetworkTicket.InvitedCompanyCountryCode,
										VatNumber: this.publicInviteCompanyForNetworkTicket.InvitedCompanyVatNumber,
										LanguageCode: this.publicInviteCompanyForNetworkTicket.InvitedCompanyLanguageCode,
										Name: this.publicInviteCompanyForNetworkTicket.InvitedCompanyName,
										TimeZone: this.publicInviteCompanyForNetworkTicket.InvitedCompanyTimeZone,
									};
								}

								this.registerUserInput.Name = this.invitedUser.Name;
								this.registerUserInput.Email = this.invitedUserEmail;
								this.registerUserInput.TimeZone = this.invitedCompany.TimeZone;

								this.registerUserInput.LanguageCode = this.invitedCompany.LanguageCode;
								if (!!getCompanyInvitationTicketResult.Data.InvitedExistingUser)
									this.registerUserInput.LanguageCode = getCompanyInvitationTicketResult.Data.InvitedExistingUser.LanguageCode;

								this.pageDisplayLanguageCode = this.registerUserInput.LanguageCode;

								this.invitedUser = <User>{
									Email: this.publicInviteCompanyForNetworkTicket.InvitedEmail,
									Name: this.publicInviteCompanyForNetworkTicket.InvitedUserName,
								};

								this.isPerformingRequest = false;
							}
						});
					} else {
						this.toasterService.showError(
							this.translateService.translate('COULD_NOT_GET_INVITATION_CONTEXT_PARAMETERS', 'list-errorCodes')
						);
					}
				}
			}
		});
	}

	public onDocumentsUpdated(documents: {
		legalAcceptance: CompanyLegalAcceptance;
		personalDataConsent: PersonalDataConsentLinkAcceptance;
	}): void {
		this.legalUserAcceptance = documents.legalAcceptance;
		this.personalDataConsentDocumentAcceptance = documents.personalDataConsent;
	}

	public onPageDisplayLanguageCodeChange(languageCode: string): void {
		this.pageDisplayLanguageCode = languageCode;
	}

	public onRefuseInvitation(event: { registerUserInput: RegisterUserInput }): void {
		if (event.registerUserInput) {
			this.appContext.setApplicationLanguageCode(event.registerUserInput.LanguageCode);
		}
		this.isSubmittingRefuse = true;

		let refuseCompanyInviteForConfirmingNetwork: RefuseNewCompanyInviteForConfirmingNetwork = <RefuseNewCompanyInviteForConfirmingNetwork>{
			InvitationId: this.invitationId,
			InvitedUserEmail: this.invitedUserEmail,
			Token: this.token.Id,
		};

		const inviteCompanyForConfirmingNetworkServiceCall = this.invitationsApiService.refuseNewCompanyInviteForConfirmingNetwork(
			refuseCompanyInviteForConfirmingNetwork
		);

		inviteCompanyForConfirmingNetworkServiceCall.subscribe({
			next: (operationResult: OperationResult<boolean>) => {
				this.isSubmittingRefuse = false;
				if (operationResult.IsValid) {
					this.processValidRefuseCompanyInviteForConfirmingNetworkResult();
				} else {
					this.processInvalidRefuseCompanyInviteForConfirmingNetworkResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isSubmittingRefuse = false;
				this.logger.error(
					'[CompanyConfirmingInvitationRefuseComponent::acceptCompanyInviteForConfirmingNetwork] HttpErrorResponse: ',
					httpErrorResponse
				);

				this.toasterService.showError(
					'[CompanyConfirmingInvitationRefuseComponent::acceptCompanyInviteForConfirmingNetwork] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	public onAcceptInvitation(event: { registerUserInput: RegisterUserInput; invitedCompany: Company | null }): void {
		if (event.registerUserInput) {
			this.appContext.setApplicationLanguageCode(event.registerUserInput.LanguageCode);
		}
		this.isSubmittingAccept = true;

		let acceptCompanyInviteForConfirmingNetwork: AcceptNewCompanyInviteForConfirmingNetwork = <AcceptNewCompanyInviteForConfirmingNetwork>{
			Token: this.token.Id,
			TermsAndConditionsDocumentAcceptanceHash: this.legalUserAcceptance?.AcceptanceHash,
			TermsAndConditionsDocumentId: this.legalUserAcceptance?.DocumentId,
			InvitationId: this.invitationId,
			NewUser: event.registerUserInput,
			NewCompany: event.invitedCompany || this.invitedCompany,
			InvitedUserEmail: this.invitedUserEmail,
			PersonalDataConsentDocumentId: this.personalDataConsentDocumentAcceptance?.DocumentId,
			LanguageCode: event.registerUserInput.LanguageCode,
		};

		const inviteCompanyForConfirmingNetworkServiceCall = this.invitationsApiService.acceptNewBuyerInviteForConfirmingNetwork(
			acceptCompanyInviteForConfirmingNetwork
		);

		inviteCompanyForConfirmingNetworkServiceCall.subscribe({
			next: (operationResult: OperationResult<boolean>) => {
				this.isSubmittingAccept = false;
				if (operationResult.IsValid) {
					this.processValidAcceptCompanyInviteForConfirmingNetworkResult();
				} else {
					this.processInvalidAcceptCompanyInviteForConfirmingNetworkResult(operationResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.isSubmittingAccept = false;
				this.logger.error(
					'[CompanyConfirmingInvitationAcceptComponent::acceptCompanyInviteForConfirmingNetwork] HttpErrorResponse: ',
					httpErrorResponse
				);

				this.toasterService.showError(
					'[CompanyConfirmingInvitationAcceptComponent::acceptCompanyInviteForConfirmingNetwork] HttpErrorResponse: ' +
						httpErrorResponse.error +
						httpErrorResponse.message
				);
			},
		});
	}

	private processInvalidRefuseCompanyInviteForConfirmingNetworkResult(operationResult: OperationResult<boolean>): void {
		operationResult.Errors?.forEach((e) =>
			this.toasterService.showError(this.translateService.translateByLanguage(e.Code, this.pageDisplayLanguageCode!, 'list-errorCodes'))
		);
	}

	private processValidRefuseCompanyInviteForConfirmingNetworkResult(): void {
		this.sucessSubmissionContent = this.translateService.translateByLanguage(
			'company-confirming-invitation-response.modalSucessInformationContentRefuse',
			this.pageDisplayLanguageCode || undefined!,
			'client'
		);
		this.sucessSubmissionButtonText = this.translateService.translateByLanguage(
			'company-confirming-invitation-response.modalInformationConfirmActionTextRefuse',
			this.pageDisplayLanguageCode || undefined!,
			'client'
		);
		this.showSucessSubmission = true;
	}

	private processValidAcceptCompanyInviteForConfirmingNetworkResult(): void {
		this.sucessSubmissionContent = this.translateService.translateByLanguage(
			'company-confirming-invitation-response.modalSucessInformationContentAccept',
			this.pageDisplayLanguageCode || undefined!,
			'client'
		);
		this.sucessSubmissionButtonText = this.translateService.translateByLanguage(
			'company-confirming-invitation-response.modalInformationConfirmActionTextAccept',
			this.pageDisplayLanguageCode || undefined!,
			'client'
		);
		this.showSucessSubmission = true;
	}

	private processInvalidAcceptCompanyInviteForConfirmingNetworkResult(operationResult: OperationResult<boolean>): void {
		operationResult.Errors?.forEach((e) =>
			this.toasterService.showError(this.translateService.translateByLanguage(e.Code, this.pageDisplayLanguageCode!, 'list-errorCodes'))
		);
	}

	private retrivedTokenAsExpiredMessages(): void {
		this.token.expiredContent = this.translateService.translateByLanguage(
			'company-confirming-invitation-response.modalInformationTokenAsExpiredContent',
			this.pageDisplayLanguageCode || undefined!,
			'client'
		);
	}

	private retrivedTokenUsedMessages(): void {
		this.token.usedContent = this.translateService.translateByLanguage(
			'company-confirming-invitation-response.modalInformationTokenUsedContent',
			this.pageDisplayLanguageCode || undefined!,
			'client'
		);
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>) {
		const errors = operationResult.Errors;
		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	private showInvitationContextInformationWhenTokenUsedOrExpired(
		getCompanyInvitationTicketResult: OperationResult<PublicInviteCompanyForNetworkTicket>
	): void {
		if (!getCompanyInvitationTicketResult.IsValid) this.processInvalidResult(getCompanyInvitationTicketResult);

		if (getCompanyInvitationTicketResult.IsValid) {
			this.invitedUser = <User>{
				Email: this.publicInviteCompanyForNetworkTicket.InvitedEmail,
				Name: this.publicInviteCompanyForNetworkTicket.InvitedUserName,
			};

			this.invitedCompany = <Company>{
				CountryCode: getCompanyInvitationTicketResult.Data.InvitedCompanyCountryCode,
				VatNumber: getCompanyInvitationTicketResult.Data.InvitedCompanyVatNumber,
				Name: getCompanyInvitationTicketResult.Data.InvitedCompanyName,
				LanguageCode: getCompanyInvitationTicketResult.Data.InvitedCompanyLanguageCode,
				TimeZone: getCompanyInvitationTicketResult.Data.InvitedCompanyTimeZone,
			};

			this.invitingCompany = getCompanyInvitationTicketResult.Data.InvitingCompany;

			this.registerUserInput.TimeZone = this.invitedCompany.TimeZone;
			this.registerUserInput.LanguageCode = this.invitedCompany.LanguageCode;

			if (!!getCompanyInvitationTicketResult.Data.InvitedExistingUser) {
				this.registerUserInput.LanguageCode = getCompanyInvitationTicketResult.Data.InvitedExistingUser.LanguageCode;
			}

			this.pageDisplayLanguageCode = this.registerUserInput.LanguageCode;
		}
	}
}
